import React from 'react';
import { useRouter } from 'expo-router';

import { logger as LOG } from '@garnish/logger';

import { useNavigationReset } from '../../routing';

// ─────────────────────────────────────────────────────────────────────────────

// https://sweetgreen.atlassian.net/wiki/spaces/DES/pages/4469325825/Order+-+Reorder+Frontend+Flow
export const useReorderNavigation = () => {
  const router = useRouter();
  const resetNavigation = useNavigationReset();

  // ─── Navigate To Modal Stack ────────────────────────────────────────────────

  const navigateToActiveBagWarning = React.useCallback(() => {
    logger.info('Navigate to ReorderActiveBagWarning');

    router.navigate('/reorder-active-bag-warning');
  }, [router]);

  const navigateToConfirmLocation = React.useCallback(
    (params?: { referrer?: string }) => {
      logger.info('Navigate to ReorderConfirmLocation');

      router.navigate({
        pathname: '/reorder-confirm-location',
        params: params?.referrer ? { referrer: params.referrer } : undefined,
      });
    },
    [router],
  );

  const navigateToChangeLocation = React.useCallback(() => {
    logger.info('Navigate to ReorderChangeLocation');

    router.navigate('/reorder-change-location');
  }, [router]);

  const navigateToConflictReview = React.useCallback(() => {
    logger.info('Navigate to ReorderConflictReview');

    router.navigate('/reorder-conflict-review');
  }, [router]);

  // ─── Replace Current Route In Modal Stack ───────────────────────────────────

  const replaceWithConfirmLocation = React.useCallback(
    (params?: { referrer?: string }) => {
      logger.info('Replace with ReorderConfirmLocation');

      router.replace({
        pathname: '/reorder-confirm-location',
        params: params?.referrer ? { referrer: params.referrer } : undefined,
      });
    },
    [router],
  );

  const replaceWithChangeLocation = React.useCallback(() => {
    logger.info('Replace with ReorderChangeLocation');

    router.replace('/reorder-change-location');
  }, [router]);

  const replaceWithConflictReview = React.useCallback(() => {
    logger.info('Replace with ReorderConflictReview');

    router.replace('/reorder-conflict-review');
  }, [router]);

  // ─── Dismiss / Finish ───────────────────────────────────────────────────────

  const dismissReorder = React.useCallback(() => {
    logger.info('Dismiss Reorder');

    if (router.canDismiss()) router.dismiss();
    else router.back();
  }, [router]);

  const finishReorder = React.useCallback(
    (restaurantSlug?: string, addressId?: string) => {
      logger.info('Reset To Bag', restaurantSlug, addressId);

      resetNavStack(resetNavigation, { restaurantSlug, addressId }, true);
    },
    [resetNavigation],
  );

  const dismissReorderNewMenu = React.useCallback(
    (restaurantSlug?: string, addressId?: string) => {
      logger.info('Reset to Menu', restaurantSlug, addressId);

      resetNavStack(resetNavigation, { restaurantSlug, addressId }, false);
    },
    [resetNavigation],
  );

  const dismissReorderToMenu = React.useCallback(
    (restaurantSlug?: string, addressId?: string) => {
      logger.info('Dismiss Reorder to Menu');

      resetNavStack(resetNavigation, { restaurantSlug, addressId }, false);
    },
    [resetNavigation],
  );

  const dismissReorderHome = React.useCallback(() => {
    router.dismissTo('/');
  }, [router]);

  return {
    navigateToActiveBagWarning,
    navigateToConfirmLocation,
    navigateToChangeLocation,
    navigateToConflictReview,
    replaceWithConfirmLocation,
    replaceWithChangeLocation,
    replaceWithConflictReview,
    dismissReorder,
    finishReorder,
    dismissReorderNewMenu,
    dismissReorderToMenu,
    dismissReorderHome,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Resets the entire navigation state to:
 *  - (tabs)/(menu)/<restaurant> showing the new restaurant menu,
 *  - (modals)/bag presented on top (optional).
 */
function resetNavStack(reset: NavigationReset, params: MenuNavigationParams, withBag = false) {
  const menuRoute = getMenuRoute(params);

  const tabsStack: NavigationResetStateRoute = {
    name: '(tabs)',
    state: { routes: [{ name: '(menu)', state: { routes: [menuRoute] } }] },
  };
  const bagModalStack: NavigationResetStateRoute = {
    name: '(modals)',
    state: { routes: [{ name: 'bag/index' }] },
  };

  const routes = withBag ? [tabsStack, bagModalStack] : [tabsStack];

  reset({ index: routes.length - 1, routes });
}

function getMenuRoute(params: MenuNavigationParams): NavigationNestedRoute {
  const { restaurantSlug, addressId } = params;

  if (addressId) {
    return { name: 'delivery/[addressId]/menu/index', params: { addressId } };
  }

  if (restaurantSlug) {
    return { name: '[restaurantSlug]/menu/index', params: { restaurantSlug } };
  }

  return { name: 'locations/index' };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const LOGGER_NAME = 'Reorder navigation';

const logger = LOG.extend(LOGGER_NAME);

LOG.disable(LOGGER_NAME);

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuNavigationParams = { restaurantSlug?: string; addressId?: string };

type NavigationNestedRoute = NonNullable<NavigationResetStateRoute['state']>['routes'][number];
type NavigationResetStateRoute = NavigationResetState['routes'][number];
type NavigationResetState = Parameters<NavigationReset>[0];
type NavigationReset = ReturnType<typeof useNavigationReset>;
